import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CardContent,
  Card,
  CssBaseline,
  Input,
  Sheet,
  ThemeProvider,
  Typography,
} from '@mui/joy';
import { Container } from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { theme } from '../../main/theme/constants';

/**
 * Public, unauthenticated landing page that lets anyone paste a piece barcode
 * and jump to the read-only tracking screen at `/track/<barcode>`. Useful as
 * the destination for QR codes printed on labels and for share-able tracking
 * links sent over WhatsApp.
 */
export default function PublicPieceTrackingLandingScreen() {
  const { t } = useTranslation('ui');
  const navigate = useNavigate();
  const [barcode, setBarcode] = useState('');
  const [error, setError] = useState(null);

  const submit = useCallback(() => {
    const code = String(barcode || '').trim();
    if (!code) {
      setError(t('publicPieceTracking.barcode'));
      return;
    }
    setError(null);
    navigate(`/track/${encodeURIComponent(code)}`);
  }, [barcode, navigate, t]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
          py: { xs: 4, sm: 8 },
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '20px',
                bgcolor: '#EDE9FE',
                mx: 'auto',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LocalShippingIcon sx={{ color: '#5f09c2', fontSize: 36 }} />
            </Box>
            <Typography level="h2" sx={{ fontWeight: 800, color: '#1f2937' }}>
              {t('publicPieceTracking.pageTitle')}
            </Typography>
            <Typography level="body-sm" sx={{ color: '#6b7280', mt: 0.5 }}>
              {t('publicPieceTracking.subtitle')}
            </Typography>
          </Box>

          <Card variant="outlined" sx={{ borderRadius: '14px' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box component="form" onSubmit={(e) => { e.preventDefault(); submit(); }}>
                <Typography level="body-sm" sx={{ fontWeight: 600, mb: 1 }}>
                  {t('publicPieceTracking.barcode')}
                </Typography>
                <Input
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  placeholder={t('scanPiece.scanInputPlaceholder')}
                  startDecorator={<QrCodeScannerIcon sx={{ color: '#5f09c2' }} />}
                  size="lg"
                  color={error ? 'danger' : 'neutral'}
                  sx={{ fontFamily: 'monospace' }}
                  autoFocus
                />
                {error && (
                  <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                    {error}
                  </Typography>
                )}
                <Button
                  type="submit"
                  size="lg"
                  fullWidth
                  sx={{ mt: 2 }}
                  startDecorator={<LocalShippingIcon />}
                  disabled={!barcode.trim()}
                >
                  {t('publicPieceTracking.pageTitle')}
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Sheet
            variant="soft"
            color="neutral"
            sx={{ mt: 3, p: 2, borderRadius: '12px', textAlign: 'center' }}
          >
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              {t('barcodePrefix.hint')}
            </Typography>
          </Sheet>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
